"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { cn } from "@/lib/_utils";

const MeshGradient = dynamic(
  () =>
    import("@paper-design/shaders-react").then((mod) => ({
      default: mod.MeshGradient,
    })),
  {
    ssr: false,
  }
);

// Color presets using your brand colors
// Hex values computed from oklch colors:
// custom-blue: oklch(60% 0.16 246.32) ≈ #3B5FE8
// custom-blue-dark: oklch(40% 0.16 246.32) ≈ #1A3A9E
// custom-blue-light: oklch(80% 0.16 246.32) ≈ #7A9FFF
// custom-yellow: oklch(0.83 0.17 80.51) ≈ #F5D547
// custom-yellow-dark: oklab(0.77 0.05 0.18) ≈ #C9A83A
const COLOR_PRESETS = {
  primary: ["#3B5FE8", "#1A3A9E", "#3B5FE8", "#7A9FFF"],
  secondary: ["#F5D547", "#C9A83A", "#F5D547", "#C9A83A"],
  tertiary: ["#7A9FFF", "#3B5FE8", "#7A9FFF", "#3B5FE8"],
  quaternary: ["#3B5FE8", "#F5D547", "#1A3A9E", "#C9A83A"],
  blue: ["#3B5FE8", "#1A3A9E", "#3B5FE8", "#7A9FFF"],
  blueDark: ["#1A3A9E", "#3B5FE8", "#1A3A9E", "#3B5FE8"],
  yellow: ["#F5D547", "#facc15", "#F5D547", "#facc15"],
  yellowDark: ["#ca8a04", "#F5D547", "#ca8a04", "#facc15"],
  cloud: ["#e5e7eb", "#e4e4e7", "#e5e5e5", "#e7e5e4"],
  cloudDark: ["#e5e7eb", "#e4e4e7", "#a3a3a3", "#e7e5e4"],
} as const;

export type MeshGradientColor = keyof typeof COLOR_PRESETS;

// Helper to convert CSS variable to hex string
// Similar to getComputedColor in button.tsx but returns hex instead of RGB
const getComputedColorHex = (varName: string): string => {
  if (typeof window === "undefined") {
    // SSR fallback
    return "#3B82F6";
  }

  const computed = getComputedStyle(document.documentElement)
    .getPropertyValue(`--color-${varName}`)
    .trim();

  // If it's already a hex color, return it
  if (computed.startsWith("#")) {
    return computed;
  }

  // Create a temp element to get computed RGB from oklch/rgb
  const temp = document.createElement("div");
  temp.style.color = computed;
  temp.style.position = "absolute";
  temp.style.visibility = "hidden";
  document.body.appendChild(temp);
  const rgb = getComputedStyle(temp).color;
  document.body.removeChild(temp);

  // Convert rgb(r, g, b) to hex
  if (rgb?.startsWith("rgb")) {
    const rgbMatch = rgb.match(/\d+/g);
    if (rgbMatch && rgbMatch.length >= 3) {
      const r = rgbMatch[0];
      const g = rgbMatch[1];
      const b = rgbMatch[2];
      if (r && g && b) {
        const rHex = Number.parseInt(r, 10).toString(16).padStart(2, "0");
        const gHex = Number.parseInt(g, 10).toString(16).padStart(2, "0");
        const bHex = Number.parseInt(b, 10).toString(16).padStart(2, "0");
        return `#${rHex}${gHex}${bHex}`.toUpperCase();
      }
    }
  }

  // Fallback
  return "#3B82F6";
};

// Helper to convert CSS variable string to hex
const convertCssVarToHex = (cssVar: string): string => {
  // If it's already a hex color, return it
  if (cssVar.startsWith("#")) {
    return cssVar;
  }

  // If it's a CSS variable (var(--color-...))
  if (cssVar.startsWith("var(")) {
    // Extract the variable name
    const match = cssVar.match(/var\(--color-([^)]+)\)/);
    if (match?.[1]) {
      return getComputedColorHex(match[1]);
    }
  }

  // Fallback
  return "#3B82F6";
};

export type MeshGradientBackgroundProps = {
  /**
   * Color preset to use
   * @default "primary"
   */
  color?: MeshGradientColor;
  /**
   * Custom color array (overrides color preset if provided)
   */
  colors?: string[];
  /**
   * Animation speed
   * @default 1
   */
  speed?: number;
  /**
   * Distortion amount
   * @default 0.8
   */
  distortion?: number;
  /**
   * Swirl amount
   * @default 0.1
   */
  swirl?: number;
  /**
   * Grain mixer value
   * @default 0
   */
  grainMixer?: number;
  /**
   * Grain overlay value
   * @default 0
   */
  grainOverlay?: number;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
  /**
   * Border radius
   */
  borderRadius?: string | number;
};

export function MeshGradientBackground({
  color = "primary",
  colors,
  speed = 1,
  distortion = 0.8,
  swirl = 0.1,
  grainMixer = 0,
  grainOverlay = 0,
  className,
  style,
  borderRadius,
}: MeshGradientBackgroundProps) {
  // Use custom colors if provided, otherwise use preset
  // Convert CSS variables to hex if needed
  const [gradientColors, setGradientColors] = useState<string[]>(() => {
    const rawColors = colors || [...COLOR_PRESETS[color]];
    return rawColors.map((c) => (c.startsWith("#") ? c : "#3B5FE8"));
  });

  useEffect(() => {
    const rawColors = colors || [...COLOR_PRESETS[color]];

    const converted = rawColors.map((colorValue) => {
      // If it's already a hex color, return as-is
      if (colorValue.startsWith("#")) {
        return colorValue;
      }
      // Convert CSS variables to hex
      return convertCssVarToHex(colorValue);
    });

    setGradientColors(converted);
  }, [colors, color]);

  const combinedStyle: React.CSSProperties = {
    height: "100%",
    width: "100%",
    ...(borderRadius && { borderRadius }),
    ...style,
  };

  return (
    <MeshGradient
      className={cn("sticky inset-0 top-0", className)}
      colors={gradientColors}
      distortion={distortion}
      grainMixer={grainMixer}
      grainOverlay={grainOverlay}
      speed={speed}
      style={combinedStyle}
      swirl={swirl}
    />
  );
}
